import React from "react";

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { IImportCsvResult } from "store/types";

interface ImportCsvResultModalProps {
  onClose: () => void;
  result: IImportCsvResult | null;
}

const Content: React.FC<{ result: IImportCsvResult }> = ({ result }) => {
  const hasUnknownColumns = result.unknown_columns.length > 0;
  const hasErrors = result.errors.length > 0;

  return (
    <DialogContent>
      <Stack spacing={1.5}>
        <Typography variant="body2">
          <strong>Total rows:</strong> {result.total_rows}
        </Typography>
        <Typography variant="body2">
          <strong>Imported:</strong> {result.imported}
        </Typography>
        {hasUnknownColumns && (
          <Typography variant="body2" color="text.secondary">
            <strong>Unknown columns:</strong> {result.unknown_columns.join(", ")}
          </Typography>
        )}
        {hasErrors && (
          <Stack spacing={0.5}>
            <Typography variant="body2" color="error">
              <strong>Errors:</strong> {result.errors.length} row(s) had errors
            </Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflow: "auto",
                bgcolor: "action.hover",
                borderRadius: 1,
                px: 1.5,
                py: 1
              }}
            >
              {result.errors.map((err, i) => (
                <Typography
                  key={i}
                  variant="caption"
                  component="pre"
                  sx={{ whiteSpace: "pre-wrap", mb: 0.5, color: "error.main" }}
                >
                  {typeof err === "object" && err !== null && "message" in err
                    ? String((err as { message: unknown }).message)
                    : typeof err === "string"
                      ? err
                      : JSON.stringify(err)}
                </Typography>
              ))}
            </Box>
          </Stack>
        )}
      </Stack>
    </DialogContent>
  );
};

export const ImportCsvResultModal: React.FC<ImportCsvResultModalProps> = ({ onClose, result }) => {
  return (
    <Dialog open={!!result} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>CSV Import Summary</DialogTitle>
      {result && <Content result={result} />}
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
